import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { FaStar, FaEdit, FaTrash, FaPlus, FaSave, FaTimes } from 'react-icons/fa';
import authService from '../services/authService';

const PromptManager = () => {
    const { user } = useAuth();
    const [prompts, setPrompts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // États pour la création/édition
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [editingPrompt, setEditingPrompt] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        content: '',
        category_id: '',
        service_type: 'llm' // Nouveau champ pour séparer LLM et Analysis
    });
    
    // États pour les prompts personnalisés
    const [customContent, setCustomContent] = useState('');
    const [showCustomForm, setShowCustomForm] = useState(false);
    const [selectedPrompt, setSelectedPrompt] = useState(null);
    
    // États pour la gestion admin
    const [selectedUser, setSelectedUser] = useState(null);
    const [users, setUsers] = useState([]);
    const [serviceFilter, setServiceFilter] = useState('all'); // all, llm, analysis
    const [showSystemPrompts, setShowSystemPrompts] = useState(false);
    const [systemPrompts, setSystemPrompts] = useState([]);
    
    // Vérifier si l'utilisateur est admin
    const isAdmin = user?.role === 'admin';

    useEffect(() => {
        if (user) {
            fetchPrompts();
            fetchCategories();
            if (isAdmin) {
                fetchUsers();
                fetchSystemPrompts();
            }
        }
    }, [user, selectedUser, serviceFilter]);

    const fetchPrompts = async () => {
        try {
            const targetUserId = isAdmin && selectedUser ? selectedUser.id : user.id;
            let url = `/api/prompts?user_id=${targetUserId}`;
            
            // Ajouter le filtre de service
            if (serviceFilter !== 'all') {
                url += `&service_type=${serviceFilter}`;
            }
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setPrompts(data);
            } else {
                setError('Erreur lors du chargement des prompts');
            }
        } catch (error) {
            setError('Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const response = await fetch('/api/prompts/categories');
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des catégories:', error);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await fetch('/api/admin/users', {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des utilisateurs:', error);
        }
    };

    const fetchSystemPrompts = async () => {
        try {
            const response = await fetch('/api/prompts/system', {
                headers: {
                    'Authorization': `Bearer ${authService.getToken()}`
                }
            });
            if (response.ok) {
                const data = await response.json();
                setSystemPrompts(data);
            }
        } catch (error) {
            console.error('Erreur lors du chargement des prompts système:', error);
        }
    };

    const handleCreatePrompt = async (e) => {
        e.preventDefault();
        try {
            const targetUserId = isAdmin && selectedUser ? selectedUser.id : user.id;
            const response = await fetch('/api/prompts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authService.getToken()}`
                },
                body: JSON.stringify({
                    ...formData,
                    user_id: targetUserId,
                    service_type: formData.service_type || 'llm',
                    category_id: parseInt(formData.category_id, 10)
                }),
            });

            if (response.ok) {
                setShowCreateForm(false);
                setFormData({ name: '', description: '', content: '', category_id: '', service_type: 'llm' });
                fetchPrompts();
            } else {
                setError('Erreur lors de la création du prompt');
            }
        } catch (error) {
            setError('Erreur de connexion');
        }
    };

    const handleUpdatePrompt = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`/api/prompts/${editingPrompt.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authService.getToken()}`
                },
                body: JSON.stringify({
                    ...formData,
                    service_type: formData.service_type || 'llm',
                    category_id: parseInt(formData.category_id, 10)
                }),
            });

            if (response.ok) {
                setEditingPrompt(null);
                setFormData({ name: '', description: '', content: '', category_id: '', service_type: 'llm' });
                fetchPrompts();
            } else {
                setError('Erreur lors de la mise à jour du prompt');
            }
        } catch (error) {
            setError('Erreur de connexion');
        }
    };

    const handleDeletePrompt = async (promptId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer ce prompt ?')) {
            try {
                const response = await fetch(`/api/prompts/${promptId}`, {
                    method: 'DELETE',
                    headers: {
                        'Authorization': `Bearer ${authService.getToken()}`
                    }
                });

                if (response.ok) {
                    fetchPrompts();
                } else {
                    setError('Erreur lors de la suppression du prompt');
                }
            } catch (error) {
                setError('Erreur de connexion');
            }
        }
    };

    const handleToggleFavorite = async (promptId) => {
        try {
            const response = await fetch(`/api/prompts/${promptId}/favorite`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authService.getToken()}`
                },
                body: JSON.stringify({ user_id: user.id }),
            });

            if (response.ok) {
                fetchPrompts();
            }
        } catch (error) {
            setError('Erreur lors du basculement du favori');
        }
    };

    const handleSetCustomContent = async () => {
        try {
            const response = await fetch(`/api/prompts/${selectedPrompt.id}/custom`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authService.getToken()}`
                },
                body: JSON.stringify({
                    user_id: user.id,
                    custom_content: customContent
                }),
            });

            if (response.ok) {
                setShowCustomForm(false);
                setCustomContent('');
                setSelectedPrompt(null);
                fetchPrompts();
            } else {
                setError('Erreur lors de la définition du contenu personnalisé');
            }
        } catch (error) {
            setError('Erreur de connexion');
        }
    };

    const startEdit = (prompt) => {
        setEditingPrompt(prompt);
        setFormData({
            name: prompt.name,
            description: prompt.description,
            content: prompt.content,
            category_id: prompt.category_id || '',
            service_type: prompt.service_type || 'llm'
        });
    };

    const startCustomEdit = (prompt) => {
        setSelectedPrompt(prompt);
        setCustomContent(prompt.custom_content || prompt.content);
        setShowCustomForm(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement des prompts...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">
                        Gestionnaire de Prompts
                        {isAdmin && <span className="ml-2 text-sm bg-red-100 text-red-800 px-2 py-1 rounded">Admin</span>}
                    </h1>
                    <p className="text-gray-600">
                        {isAdmin ? 'Gérez les prompts pour tous les utilisateurs et services' : 'Gérez vos prompts personnalisés pour l\'assistant IA'}
                    </p>
                </div>

                {error && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                        <p className="text-red-800">{error}</p>
                    </div>
                )}

                {/* Contrôles Admin */}
                {isAdmin && (
                    <div className="mb-6 bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Contrôles Administrateur</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Sélection utilisateur */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Utilisateur</label>
                                <select
                                    value={selectedUser?.id || ''}
                                    onChange={(e) => {
                                        const userId = e.target.value;
                                        const user = users.find(u => u.id === parseInt(userId));
                                        setSelectedUser(user || null);
                                    }}
                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="">Tous les utilisateurs</option>
                                    {users.map(u => (
                                        <option key={u.id} value={u.id}>
                                            {u.email} ({u.full_name})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Filtre service */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Service</label>
                                <select
                                    value={serviceFilter}
                                    onChange={(e) => setServiceFilter(e.target.value)}
                                    className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                >
                                    <option value="all">Tous les services</option>
                                    <option value="llm">Service LLM</option>
                                    <option value="analysis">Service Analysis</option>
                                </select>
                            </div>

                            {/* Toggle prompts système */}
                            <div className="flex items-end">
                                <button
                                    onClick={() => setShowSystemPrompts(!showSystemPrompts)}
                                    className={`px-4 py-2 rounded-md text-sm font-medium ${
                                        showSystemPrompts
                                            ? 'bg-green-600 text-white'
                                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                    }`}
                                >
                                    {showSystemPrompts ? 'Masquer' : 'Voir'} Prompts Système
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Boutons d'action */}
                <div className="mb-6 flex space-x-4">
                    <button
                        onClick={() => setShowCreateForm(true)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        <FaPlus className="mr-2" />
                        Nouveau Prompt
                    </button>
                    
                    {isAdmin && (
                        <div className="text-sm text-gray-600 flex items-center">
                            Gérant {selectedUser ? `les prompts de ${selectedUser.email}` : 'tous les prompts'}
                            {serviceFilter !== 'all' && ` pour le service ${serviceFilter}`}
                        </div>
                    )}
                </div>

                {/* Formulaire de création */}
                {showCreateForm && (
                    <div className="mb-8 bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Créer un nouveau prompt</h3>
                        <form onSubmit={handleCreatePrompt}>
                            <div className="grid grid-cols-1 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nom</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Description</label>
                                    <input
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Catégorie</label>
                                    <select
                                        value={formData.category_id}
                                        onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="">Sélectionner une catégorie</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Type de Service</label>
                                    <select
                                        value={formData.service_type}
                                        onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="llm">Service LLM</option>
                                        <option value="analysis">Service Analysis</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Contenu</label>
                                    <textarea
                                        value={formData.content}
                                        onChange={(e) => setFormData({...formData, content: e.target.value})}
                                        rows={6}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateForm(false)}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <FaTimes className="mr-2" />
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <FaSave className="mr-2" />
                                    Créer
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Formulaire d'édition */}
                {editingPrompt && (
                    <div className="mb-8 bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Modifier le prompt</h3>
                        <form onSubmit={handleUpdatePrompt}>
                            <div className="grid grid-cols-1 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nom</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Description</label>
                                    <input
                                        type="text"
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Catégorie</label>
                                    <select
                                        value={formData.category_id}
                                        onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="">Sélectionner une catégorie</option>
                                        {categories.map(category => (
                                            <option key={category.id} value={category.id}>
                                                {category.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Type de Service</label>
                                    <select
                                        value={formData.service_type}
                                        onChange={(e) => setFormData({...formData, service_type: e.target.value})}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    >
                                        <option value="llm">Service LLM</option>
                                        <option value="analysis">Service Analysis</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Contenu</label>
                                    <textarea
                                        value={formData.content}
                                        onChange={(e) => setFormData({...formData, content: e.target.value})}
                                        rows={6}
                                        className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                        required
                                    />
                                </div>
                            </div>
                            <div className="mt-6 flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => setEditingPrompt(null)}
                                    className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <FaTimes className="mr-2" />
                                    Annuler
                                </button>
                                <button
                                    type="submit"
                                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                                >
                                    <FaSave className="mr-2" />
                                    Sauvegarder
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Formulaire de contenu personnalisé */}
                {showCustomForm && selectedPrompt && (
                    <div className="mb-8 bg-white rounded-lg shadow p-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">
                            Personnaliser le prompt: {selectedPrompt.name}
                        </h3>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Contenu personnalisé</label>
                            <textarea
                                value={customContent}
                                onChange={(e) => setCustomContent(e.target.value)}
                                rows={6}
                                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Entrez votre version personnalisée du prompt..."
                            />
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => setShowCustomForm(false)}
                                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                                <FaTimes className="mr-2" />
                                Annuler
                            </button>
                            <button
                                onClick={handleSetCustomContent}
                                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700"
                            >
                                <FaSave className="mr-2" />
                                Sauvegarder
                            </button>
                        </div>
                    </div>
                )}

                {/* Prompts système (admin seulement) */}
                {isAdmin && showSystemPrompts && (
                    <div className="mb-8">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">Prompts Système</h2>
                        <div className="grid grid-cols-1 gap-4">
                            {systemPrompts.map((prompt) => (
                                <div key={prompt.id} className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                                    <div className="flex items-start justify-between">
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-2">
                                                <h4 className="text-lg font-medium text-gray-900">{prompt.name}</h4>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    Système
                                                </span>
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                    {prompt.service_type === 'llm' ? 'LLM' : 'Analysis'}
                                                </span>
                                            </div>
                                            <p className="text-gray-600 mb-2">{prompt.description}</p>
                                            {prompt.category_name && (
                                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mb-3">
                                                    {prompt.category_name}
                                                </span>
                                            )}
                                            <div className="bg-white rounded-md p-3 border">
                                                <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                                    {prompt.content}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {systemPrompts.length === 0 && (
                            <div className="text-center py-8">
                                <p className="text-gray-500">Aucun prompt système disponible</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Liste des prompts */}
                <div className="grid grid-cols-1 gap-6">
                    {prompts.map((prompt) => (
                        <div key={prompt.id} className="bg-white rounded-lg shadow p-6">
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-2">
                                        <h3 className="text-lg font-medium text-gray-900">{prompt.name}</h3>
                                        {prompt.is_favorite && (
                                            <FaStar className="text-yellow-400" />
                                        )}
                                        {prompt.is_default && (
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                Par défaut
                                            </span>
                                        )}
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            {prompt.service_type === 'llm' ? 'LLM' : 'Analysis'}
                                        </span>
                                    </div>
                                    <p className="text-gray-600 mb-2">{prompt.description}</p>
                                    {prompt.category_name && (
                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mb-3">
                                            {prompt.category_name}
                                        </span>
                                    )}
                                    <div className="bg-gray-50 rounded-md p-3 mb-4">
                                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                                            {prompt.custom_content || prompt.content}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => handleToggleFavorite(prompt.id)}
                                        className={`p-2 rounded-md ${
                                            prompt.is_favorite 
                                                ? 'text-yellow-400 hover:text-yellow-500' 
                                                : 'text-gray-400 hover:text-yellow-400'
                                        }`}
                                        title={prompt.is_favorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
                                    >
                                        <FaStar />
                                    </button>
                                    <button
                                        onClick={() => startCustomEdit(prompt)}
                                        className="p-2 text-blue-600 hover:text-blue-700 rounded-md"
                                        title="Personnaliser"
                                    >
                                        <FaEdit />
                                    </button>
                                    {!prompt.is_default && (
                                        <>
                                            <button
                                                onClick={() => startEdit(prompt)}
                                                className="p-2 text-green-600 hover:text-green-700 rounded-md"
                                                title="Modifier"
                                            >
                                                <FaEdit />
                                            </button>
                                            <button
                                                onClick={() => handleDeletePrompt(prompt.id)}
                                                className="p-2 text-red-600 hover:text-red-700 rounded-md"
                                                title="Supprimer"
                                            >
                                                <FaTrash />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {prompts.length === 0 && !loading && (
                    <div className="text-center py-12">
                        <p className="text-gray-500">Aucun prompt disponible</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PromptManager; 